"use client";

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Stat {
  id: string;
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

export default function SuccessStatistics() {
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const stats: Stat[] = [
    {
      id: 'stat_exported',
      value: 500,
      suffix: '+ kg',
      label: 'Gold Exported Annually',
      icon: 'ScaleIcon',
    },
    {
      id: 'stat_countries',
      value: 15,
      suffix: '+',
      label: 'Countries Served',
      icon: 'GlobeAltIcon',
    },
    {
      id: 'stat_clients',
      value: 50,
      suffix: '+',
      label: 'Active Buyers',
      icon: 'UsersIcon',
    },
    {
      id: 'stat_purity',
      value: 99.9,
      suffix: '%',
      label: 'Purity Guarantee',
      icon: 'ShieldCheckIcon',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            stats.forEach((stat) => {
              const element = document.getElementById(`metric-why-${stat.id}`);
              if (element) {
                animateValue(element, 0, stat.value, 1500, stat.suffix);
              }
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  function animateValue(obj: HTMLElement, start: number, end: number, duration: number, suffix: string) {
    let startTimestamp: number | null = null;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const val = progress * (end - start) + start;
      obj.innerHTML = (end % 1 === 0 ? Math.floor(val) : val.toFixed(1)) + suffix;
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }

  return (
    <section ref={sectionRef} className="py-32 bg-card">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif italic font-bold tracking-tight mb-4 text-foreground">
            Our Track Record
          </h2>
          <p className="text-lg text-muted max-w-2xl mx-auto">
            Numbers that demonstrate our reliability and scale
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div key={stat.id} className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto">
                <Icon name={stat.icon as any} size={32} className="text-primary-foreground" />
              </div>
              <p
                id={`metric-why-${stat.id}`}
                className="text-5xl font-bold tracking-tighter text-primary"
              >
                0{stat.suffix}
              </p>
              <p className="font-medium text-foreground uppercase tracking-widest text-sm">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}